import type { ListViewPath } from "_shared/_views/list-pages/current-table-view";
import {
  readListChips,
  writeListChips,
  type ListChipGroup,
  type ListChipSelection,
} from "_shared/_views/list-pages/list-chip-selection";
import type { RetiredListSegment } from "_shared/_views/list-pages/retired-list-redirect";
import type { CurrentTableViewOptions } from "_shared/_views/list-pages/use-current-table-view";
import type WorkAgreement from "work/work-agreements/work-agreement";
import WorkRecordCriteria from "work/work-records/work-record-criteria";

/**
 * The chips of the work agreement list, and how its URL spells them (#6716).
 *
 * The list had an Active and an Archived tab for one kind of thing. A tab is a different kind of
 * thing, so the tabs became one Archived chip: with no chip the list shows current work, and with
 * it the archive. Archived is the page's only stage, so it is a group of its own and narrows.
 */

/** The path of the list. It has no tab. */
export const WORK_AGREEMENTS_PATH = "/work";

/** The query parameter of the Archived chip. `WorkRecordListLink.WorkAgreementStageParameter` writes it too. */
export const STAGE_PARAMETER = "stage";

/** The Archived chip's value. */
export const ARCHIVED_STAGE = "archived";

/**
 * The stage a link writes for current work: no chip, said outright, so the reader's remembered
 * view does not replace it (#6714). No chip has it, so the page reads it as no chip.
 */
export const OPEN_STAGE = "open";

/**
 * The URL parameter, and the server's `WorkAgreementFilterParameters.CurrentVersionOnly`. Unset, the
 * list shows the latest version of each work the reader can see; `false` shows every version
 * (#6810, #7090). A link writes it; the page has no control of its own for it.
 */
export const CURRENT_VERSION_ONLY_PARAMETER = "currentVersionOnly";

/**
 * The URL parameter, and the server's `WorkAgreementFilterParameters.VersionsOf`: every version of
 * one work, named by its first agreement (#7090). A row's version badge writes it.
 */
export const VERSIONS_OF_PARAMETER = "versionsOf";

export const workAgreementChipGroups: readonly ListChipGroup[] = [
  { parameter: STAGE_PARAMETER, label: "Stage", chips: [{ value: ARCHIVED_STAGE, label: "Archived" }] },
];

/** The chips `searchParams` names. */
export function readWorkAgreementChips(searchParams: URLSearchParams): ListChipSelection {
  return readListChips(workAgreementChipGroups, searchParams);
}

/** Whether `selection` lists the archive rather than current work. */
export function listsArchivedWork(selection: ListChipSelection): boolean {
  return (selection[STAGE_PARAMETER] ?? []).includes(ARCHIVED_STAGE);
}

/**
 * `searchParams` with `selection` written over the chips. The list starts again at its first page,
 * and leaving the archive drops the versions a badge asked for, which only the archive holds.
 */
export function writeWorkAgreementChips(selection: ListChipSelection, searchParams: URLSearchParams): URLSearchParams {
  const next = writeListChips(workAgreementChipGroups, selection, searchParams);
  if (!listsArchivedWork(selection)) {
    next.delete(CURRENT_VERSION_ONLY_PARAMETER);
    next.delete(VERSIONS_OF_PARAMETER);
  }
  if (next.has("pageIndex")) next.set("pageIndex", "0");
  return next;
}

/**
 * The list a work agreement belongs to, for the pages that go back to it: the list itself for current
 * work, which opens the reader's own view of it, and the Archived chip for archived work.
 */
export function workAgreementListPath(isArchived: boolean): string {
  return isArchived ? `${WORK_AGREEMENTS_PATH}?${STAGE_PARAMETER}=${ARCHIVED_STAGE}` : WORK_AGREEMENTS_PATH;
}

/**
 * Where a row's version badge goes (#7090): the archive, narrowed to every version of the row's work,
 * which lists the earlier versions the reader can see. Undefined for a first version, which has none.
 *
 * The archive because an earlier version is archived when the next one is hired. Every version, not
 * the latest only: `WorkAgreementFilterParameters.fromSearchParams` reads a list narrowed to one work
 * as all of its versions.
 */
export function workVersionsPath(workAgreement: WorkAgreement): string | undefined {
  const work = workAgreement.conversationId;
  if ((workAgreement.versionNumber ?? 0) < 2 || !work) return undefined;
  const search = new URLSearchParams({ [STAGE_PARAMETER]: ARCHIVED_STAGE, [VERSIONS_OF_PARAMETER]: work.value });
  return `${WORK_AGREEMENTS_PATH}?${search.toString()}`;
}

/**
 * The tabs the list had before #6716. Bookmarks, notifications and the assistant's answers name
 * them, so each still opens the rows it listed, whatever view the reader kept. `/work/{active|archived}/view/:id` are other routes
 * and keep resolving.
 */
export const RETIRED_WORK_AGREEMENT_TABS: readonly RetiredListSegment[] = [
  // Current work said outright, as `OPEN_STAGE` is for new links: a bare `/work` opens the reader's
  // kept view, which can be the archive.
  { segment: "active", chips: { [STAGE_PARAMETER]: [OPEN_STAGE] } },
  { segment: "archived", chips: { [STAGE_PARAMETER]: [ARCHIVED_STAGE] } },
];

/**
 * The table's parameters a stored view of the list keeps: the versions shown, the column filters, the
 * sort and the page size. The chips and the criteria a link brought are kept too, below. The search
 * text and the page number are never kept (#6714).
 */
const KEPT_PARAMETERS: readonly string[] = [
  CURRENT_VERSION_ONLY_PARAMETER,
  "name",
  "description",
  "orderBy",
  "orderDir",
  "pageSize",
];

/**
 * A stored view as this page still reads it: on the list's own path, with a chip this page no longer
 * has dropped. A view of a tab the page no longer has is shown as the chips that tab became.
 */
export function keepWorkAgreementsView(view: ListViewPath): ListViewPath | undefined {
  const stored = new URLSearchParams(view.search);
  const retired = RETIRED_WORK_AGREEMENT_TABS.find((tab) => view.tabPath === `${WORK_AGREEMENTS_PATH}/${tab.segment}`);
  if (view.tabPath !== WORK_AGREEMENTS_PATH && retired === undefined) return undefined;

  const chips = retired?.chips ?? readWorkAgreementChips(stored);
  const kept = writeListChips(workAgreementChipGroups, chips, new URLSearchParams());
  for (const parameter of KEPT_PARAMETERS) {
    const value = stored.get(parameter);
    if (value !== null) kept.set(parameter, value);
  }
  // A view left on a list a link narrowed, to one client or a range of dates, comes back narrowed the
  // same way, as the Proposals view does; dropped, it would show unrelated work under the same sort.
  // The search box is itself a criterion, and a search is never kept.
  const narrowed = WorkRecordCriteria.WORK_AGREEMENTS.carryOver(stored, kept, ["query"]);
  return { tabPath: WORK_AGREEMENTS_PATH, search: narrowed.toString() };
}

/**
 * The page's Default view, declared once: current work, newest start first, with no chip. Its bare
 * path is the view, so a plain visit opens the reader's starred or last view, else this one.
 */
export const WORK_AGREEMENTS_CURRENT_VIEW: CurrentTableViewOptions = {
  tableKey: "work-agreements",
  basePath: WORK_AGREEMENTS_PATH,
  keepWhatStillExists: keepWorkAgreementsView,
};
